import wretch, {
  WretchOptions,
  WretchError,
  ConfiguredMiddleware,
  WretchResponse,
  WretchResponseChain,
} from 'wretch';
import QueryStringAddon from 'wretch/addons/queryString';
import type {QueryStringAddon as QueryStringAddonType} from 'wretch/addons/queryString';
import type {
  RequestInfo,
  RequestInit,
  Response,
  HeadersInit,
  Headers,
} from 'node-fetch';
import {parseJson} from './parseJson';

export type LoggerFn = (data: Record<string, unknown>, msg: string) => void;
export type Fetch = (url: RequestInfo, init?: RequestInit) => Promise<Response>;
export type ErrorCatcher = (error: WretchError) => never;
export type HeadersType = typeof Headers;

let polyfills: {
  fetch: Fetch;
  FormData: FormData;
  URLSearchParams: URLSearchParams;
};
let errorCatcherFn: ErrorCatcher;
let logMiddleware: () => ConfiguredMiddleware;
let isNodeConfigured = false;

export const setErrorCatcher = ({
  errorCatcher,
}: {
  errorCatcher: ErrorCatcher;
}) => {
  errorCatcherFn = errorCatcher;
};

export const configureNodeFetch = ({
  nodeFetch,
  logger,
  Headers,
  formData,
}: {
  nodeFetch: Fetch;
  logger: LoggerFn;
  formData: FormData;
  Headers: HeadersType;
}) => {
  // ensure this is removed from client bundle
  if (
    // @ts-ignore
    typeof __webpack_require__ !== 'function'
  ) {
    isNodeConfigured = true;
    const {performance} = require('perf_hooks');
    const URLSearchParams = require('url').URLSearchParams;
    globalThis.Headers = Headers as any;

    const getRequestRuid = (headers?: HeadersInit) => {
      // this narrows down the type of HeadersInit which can be three things
      if (!Array.isArray(headers) && !(headers instanceof Headers)) {
        return headers?.ruid;
      }
    };

    polyfills = {
      fetch(url, opts) {
        const requestStart = performance.now();
        return nodeFetch(url, opts).then((res: Response) => {
          const requestEnd = performance.now();
          logger(
            {
              url: decodeURIComponent(String(url)),
              duration: requestEnd - requestStart,
              ruid: getRequestRuid(opts?.headers),
            },
            'API Request Duration'
          );
          return res;
        });
      },
      FormData: formData,
      URLSearchParams,
    };

    logMiddleware =
      () =>
      (next) =>
      (url, opts): Promise<WretchResponse> => {
        return next(url, opts).then((res) => {
          const headers: {
            ruid: string;
            host: string;
          } = opts.headers;
          if (headers) {
            logger(
              {
                url: decodeURIComponent(res.url),
                method: opts.method,
                ruid: headers.ruid,
                host: headers.host,
                status: res.status,
                statusText: res.statusText,
              },
              'API Request'
            );
          }
          return res;
        });
      };
  }
};

function request(path: string, options: WretchOptions = {}) {
  const wretchInstance = wretch(path, options).addon(QueryStringAddon);
  if (
    // @ts-ignore
    typeof __webpack_require__ !== 'function'
  ) {
    if (!isNodeConfigured) {
      throw new Error(
        'http has not been configured for node, did you forget configureNodeFetch()?'
      );
    }
    return wretchInstance.polyfills(polyfills).middlewares([logMiddleware()]);
  }

  return wretchInstance.errorType('text');
}

type Query = Record<string, unknown>;
type GetParams = {
  path: string;
  query?: Query;
  options?: WretchOptions;
};
type PostParams = {
  path: string;
  body?: unknown;
  options?: WretchOptions;
};
type PutParams = PostParams;
type PatchParams = PostParams;

function addErrorCatcher(
  responseChain: WretchResponseChain<QueryStringAddonType, unknown, undefined>
) {
  if (!errorCatcherFn) {
    return responseChain;
  }
  [
    400, 401, 402, 403, 404, 405, 406, 408, 409, 422, 500, 501, 502, 503, 504,
  ].forEach((statusCode) => {
    responseChain.error(statusCode, errorCatcherFn);
  });
  // catches other kinds of errors, like request blocking
  responseChain.fetchError(errorCatcherFn);
  return responseChain;
}

/**
 * Low level methods
 * These require `.res()` to get access to the actual response
 * Usually used by more specific methods just as `Text` or `Json`
 * */

export function httpPost({path, body, options}: PostParams) {
  return addErrorCatcher(request(path, options).post(body));
}

export function httpPatch({path, body, options}: PatchParams) {
  return addErrorCatcher(request(path, options).patch(body));
}

export function httpPut({path, body, options}: PutParams) {
  return addErrorCatcher(request(path, options).put(body));
}

export function httpGet({path, query = {}, options}: GetParams) {
  return addErrorCatcher(request(path, options).query(query).get());
}

export function httpDelete({
  path,
  options,
}: {
  path: string;
  options?: WretchOptions;
}) {
  return addErrorCatcher(request(path, options).delete());
}

/**
 * High level methods
 */

export function httpGetText<T = string>({
  path,
  query = {},
  options,
}: GetParams): Promise<T> {
  return httpGet({path, options, query}).text();
}

export function httpGetJson<T>({
  path,
  query = {},
  options,
}: GetParams): Promise<T> {
  return httpGetText({path, query, options}).then((text) => parseJson<T>(text));
}

export function httpPostText<T = string>({
  path,
  body,
  options,
}: {
  path: string;
  body?: unknown;
  options?: WretchOptions;
}): Promise<T> {
  return httpPost({path, body, options}).text();
}

export function httpPostJson<T>({
  path,
  body,
  options,
}: {
  path: string;
  body?: unknown;
  options?: WretchOptions;
}): Promise<T> {
  return httpPostText({path, body, options}).then((text) => parseJson<T>(text));
}
